/** Support centre (D-129): helpers shared by the back-office screens. */

import { useSupportConfigRetrieve } from "@agencyos/api-client";
import { durationText, remaining, type SupportTimer, timerTone } from "@agencyos/core";
import { Badge, type BadgeTone } from "@agencyos/ui-web";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { useCurrentMembership } from "../../lib/settings";

export const CATEGORIES = ["access", "billing", "refund", "content", "technical", "immigration", "complaint", "other"] as const;
export const PRIORITIES = ["low", "normal", "high", "urgent"] as const;
export const STATUSES = ["new", "open", "waiting_customer", "waiting_internal", "resolved", "closed"] as const;
export const STAFF_CHANNELS = ["staff", "phone", "desk", "email", "inbox"] as const;

export interface SupportConfig {
  categories?: Record<string, { fr?: string; en?: string; team?: string }>;
  teams?: Record<string, { fr?: string; en?: string }>;
  caps?: { extend_max_days?: number; oral_credits_max?: number; capped?: boolean };
  attachments?: { max_bytes?: number; types?: string[] };
  can?: Record<string, boolean>;
  members?: { id: string; name: string; position: string }[];
}

/** The support data the screens read (categories, teams, caps, who may do what). */
export function useSupportConfig(): SupportConfig | undefined {
  const query = useSupportConfigRetrieve({ query: { staleTime: 60_000, retry: false } });
  return query.data?.status === 200 ? (query.data.data as SupportConfig) : undefined;
}

/** D-129: the support codes of the positions matrix, read from `/auth/me/`. */
export function useSupportPermissions(): (code: string) => boolean {
  const membership = useCurrentMembership();
  const granted = new Set(membership?.permissions ?? []);
  return (code) => granted.has("*") || granted.has(code);
}

export function useRefreshSupport() {
  const queryClient = useQueryClient();
  return () =>
    void queryClient.invalidateQueries({
      predicate: (query) => String(query.queryKey[0] ?? "").startsWith("/api/v1/support/"),
    });
}

export function TimerBadge({ timer }: { timer: SupportTimer | null | undefined }) {
  const { t, i18n } = useTranslation();
  const left = remaining(timer);
  if (left.state === "none" || !timer) return <span className="muted">{t("support.timer.none")}</span>;
  if (left.state === "paused") return <Badge tone="info">{t("support.timer.paused")}</Badge>;
  const target = t(`support.timer.${timer.target === "first_response" ? "first_response" : "resolution"}`);
  const time = durationText(left, i18n.language);
  const tone = timerTone(left) as BadgeTone;
  return <Badge tone={tone}>{t(left.state === "late" ? "support.timer.late" : "support.timer.left", { target, time })}</Badge>;
}
